<template>
    <section class="img-top-bottom-section">
        <div class="img-top-bottom-section__container">
            <CardTitle v-if="!hideTitle" :module="item" />
            <div class="img-top-bottom__grid">
                <article v-for="(cell, idx) in item.fieldData"
                    :key="String((cell as Record<string, unknown>)?.id ?? idx)" class="img-top-bottom__item"
                    @click="handleCellClick(cell)">
                    <MediaCard v-if="getCellImage(cell)" class="img-top-bottom__media" :url="getCellImage(cell)"
                        :name="getCellTitle(cell)" video-popup />
                    <p v-if="getCellTitle(cell)" class="img-top-bottom__title">
                        {{ getCellTitle(cell) }}
                    </p>
                </article>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord, jumpToUrl } from './index.js'

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}

function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}

function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) {
        jumpToUrl(url)
    }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.img-top-bottom-section {
    width: 100%;
}

.img-top-bottom-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.img-top-bottom__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem 0.75rem;

    @media (min-width: $breakpoint-md) {
        grid-template-columns: repeat(4, 1fr);
        gap: 2rem 1.25rem;
    }
}

.img-top-bottom__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    transition: transform 0.2s ease;
    max-width: 100%;

    @media (min-width: $breakpoint-md) {
        gap: 1rem;
    }

    &:hover {
        .img-top-bottom__media img {
            transform: scale(1.03);
        }
    }
}

.img-top-bottom__media {
    width: 100%;
    // aspect-ratio: 1 / 1;
    border-radius: 20px;
    overflow: hidden;
    background: #f5f5f5;

    @media (min-width: $breakpoint-md) {
        border-radius: 28px;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.3s ease;
    }
}

.img-top-bottom__title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 800;
    color: $color-text;
    text-align: center;
    line-height: 1.3;

    @media (min-width: $breakpoint-md) {
        font-size: 1.375rem;
    }
}
</style>
